import { useState } from 'react'
import { norm } from '../../utils/normalize'
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

interface Ingredient {
  id: string
  name: string
  category: string
  allergens: string[]
  unit: string
  costPerUnit: number
}

interface RecipeIngredient {
  id: string
  ingredient: Ingredient
  quantity: number
  percentage: number
  preparation?: string
  notes?: string
}

interface Recipe {
  id: string
  name: string
  code: string
  type: 'elaborated' | 'finished'
  category: string
  description: string
  yield: number
  yieldUnit: string
  preparationTime: number
  bakingTime?: number
  restTime?: number
  totalTime: number
  difficulty: 'easy' | 'medium' | 'hard'
  allergens: string[]
  ingredients: RecipeIngredient[]
  instructions: RecipeStep[]
  cost: number
  sellPrice?: number
  margin?: number
  nutritionalInfo?: NutritionalInfo
  createdBy: string
  createdAt: string
  updatedAt: string
  isActive: boolean
}

interface RecipeStep {
  id: string
  order: number
  instruction: string
  duration?: number
  temperature?: number
  equipment?: string
  notes?: string
}

interface NutritionalInfo {
  calories: number
  protein: number
  carbohydrates: number
  fat: number
  fiber: number
  sugar: number
  sodium: number
}

const mockIngredients: Ingredient[] = [
  { id: '1', name: 'Harina de Trigo', category: 'Harinas', allergens: ['Gluten'], unit: 'kg', costPerUnit: 1.20 },
  { id: '2', name: 'Azúcar Blanco', category: 'Endulzantes', allergens: [], unit: 'kg', costPerUnit: 0.95 },
  { id: '3', name: 'Mantequilla', category: 'Lácteos', allergens: ['Lácteos'], unit: 'kg', costPerUnit: 3.50 },
  { id: '4', name: 'Huevos Frescos', category: 'Proteínas', allergens: ['Huevo'], unit: 'unidad', costPerUnit: 0.35 },
  { id: '5', name: 'Chocolate Negro', category: 'Saborizantes', allergens: ['Lácteos', 'Soja'], unit: 'kg', costPerUnit: 8.50 },
  { id: '6', name: 'Vainilla', category: 'Saborizantes', allergens: [], unit: 'ml', costPerUnit: 0.15 },
  { id: '7', name: 'Sal', category: 'Condimentos', allergens: [], unit: 'kg', costPerUnit: 0.80 },
  { id: '8', name: 'Levadura', category: 'Fermentantes', allergens: [], unit: 'kg', costPerUnit: 12.00 },
]

const mockRecipes: Recipe[] = [
  {
    id: '1',
    name: 'Masa Quebrada Básica',
    code: 'MQ-001',
    type: 'elaborated',
    category: 'Masas',
    description: 'Masa quebrada básica para tartas y bases',
    yield: 1,
    yieldUnit: 'kg',
    preparationTime: 15,
    restTime: 30,
    totalTime: 45,
    difficulty: 'easy',
    allergens: ['Gluten', 'Lácteos'],
    cost: 3.85,
    ingredients: [
      {
        id: '1',
        ingredient: mockIngredients[0],
        quantity: 0.5,
        percentage: 50.0,
        preparation: 'Tamizada'
      },
      {
        id: '2',
        ingredient: mockIngredients[2],
        quantity: 0.25,
        percentage: 25.0,
        preparation: 'Temperatura ambiente'
      },
      {
        id: '3',
        ingredient: mockIngredients[1],
        quantity: 0.125,
        percentage: 12.5,
        preparation: 'Tamizado'
      },
      {
        id: '4',
        ingredient: mockIngredients[3],
        quantity: 2,
        percentage: 12.5,
        preparation: 'Batidos'
      }
    ],
    instructions: [
      {
        id: '1',
        order: 1,
        instruction: 'Mezclar la harina tamizada con el azúcar en un bowl',
        duration: 2
      },
      {
        id: '2',
        order: 2,
        instruction: 'Agregar la mantequilla fría en cubos y mezclar hasta obtener textura arenosa',
        duration: 5
      },
      {
        id: '3',
        order: 3,
        instruction: 'Incorporar los huevos batidos y formar una masa homogénea',
        duration: 3
      },
      {
        id: '4',
        order: 4,
        instruction: 'Envolver en film y refrigerar por 30 minutos',
        duration: 30
      }
    ],
    createdBy: 'Chef María',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
    isActive: true
  },
  {
    id: '2',
    name: 'Tarta de Chocolate',
    code: 'TC-001',
    type: 'finished',
    category: 'Tartas',
    description: 'Tarta de chocolate con ganache y decoración',
    yield: 8,
    yieldUnit: 'porciones',
    preparationTime: 45,
    bakingTime: 25,
    restTime: 120,
    totalTime: 190,
    difficulty: 'medium',
    allergens: ['Gluten', 'Lácteos', 'Huevo', 'Soja'],
    cost: 12.50,
    sellPrice: 25.00,
    margin: 50.0,
    ingredients: [
      {
        id: '1',
        ingredient: mockIngredients[0],
        quantity: 0.3,
        percentage: 30.0,
        preparation: 'Tamizada'
      },
      {
        id: '2',
        ingredient: mockIngredients[4],
        quantity: 0.2,
        percentage: 20.0,
        preparation: 'Derretido'
      },
      {
        id: '3',
        ingredient: mockIngredients[2],
        quantity: 0.15,
        percentage: 15.0,
        preparation: 'Temperatura ambiente'
      },
      {
        id: '4',
        ingredient: mockIngredients[1],
        quantity: 0.15,
        percentage: 15.0
      },
      {
        id: '5',
        ingredient: mockIngredients[3],
        quantity: 3,
        percentage: 20.0,
        preparation: 'Separados'
      }
    ],
    instructions: [
      {
        id: '1',
        order: 1,
        instruction: 'Precalentar el horno a 180°C',
        temperature: 180
      },
      {
        id: '2',
        order: 2,
        instruction: 'Derretir el chocolate al baño maría',
        duration: 10
      },
      {
        id: '3',
        order: 3,
        instruction: 'Batir mantequilla con azúcar hasta cremar',
        duration: 5,
        equipment: 'Batidora'
      },
      {
        id: '4',
        order: 4,
        instruction: 'Agregar yemas una a una',
        duration: 2
      },
      {
        id: '5',
        order: 5,
        instruction: 'Incorporar chocolate derretido',
        duration: 2
      },
      {
        id: '6',
        order: 6,
        instruction: 'Agregar harina tamizada alternando con claras batidas a nieve',
        duration: 5
      },
      {
        id: '7',
        order: 7,
        instruction: 'Hornear en molde enmantequillado por 25 minutos',
        duration: 25,
        temperature: 180
      },
      {
        id: '8',
        order: 8,
        instruction: 'Dejar enfriar completamente antes de desmoldar',
        duration: 120
      }
    ],
    nutritionalInfo: {
      calories: 320,
      protein: 6.5,
      carbohydrates: 35.2,
      fat: 18.8,
      fiber: 3.1,
      sugar: 28.5,
      sodium: 155
    },
    createdBy: 'Chef María',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-16T14:30:00Z',
    isActive: true
  }
]

export default function Recipes() {
  const [recipes, setRecipes] = useState<Recipe[]>(mockRecipes)
  const [selectedType, setSelectedType] = useState<'all' | 'elaborated' | 'finished'>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const categories = ['all', ...Array.from(new Set(recipes.map(recipe => recipe.category)))]

  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = norm(recipe.name).includes(norm(searchTerm)) ||
                         norm(recipe.code).includes(norm(searchTerm))
    const matchesType = selectedType === 'all' || recipe.type === selectedType
    const matchesCategory = selectedCategory === 'all' || recipe.category === selectedCategory
    return matchesSearch && matchesType && matchesCategory && recipe.isActive
  })

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-700 bg-green-100'
      case 'medium':
        return 'text-yellow-700 bg-yellow-100'
      case 'hard':
        return 'text-red-700 bg-red-100'
      default:
        return 'text-gray-700 bg-gray-100'
    }
  }

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'Fácil'
      case 'medium':
        return 'Medio'
      case 'hard':
        return 'Difícil'
      default:
        return difficulty
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'elaborated':
        return 'Elaborado'
      case 'finished':
        return 'Producto Terminado'
      default:
        return type
    }
  }

  const handleNewRecipe = () => {
    // Modal functionality removed
  }

  const handleEditRecipe = () => {
    // Modal functionality removed
  }


  const handleDeleteRecipe = (id: string) => {
    if (confirm('¿Está seguro de que desea eliminar esta receta?')) {
      setRecipes(recipes.map(recipe =>
        recipe.id === id ? { ...recipe, isActive: false } : recipe
      ))
    }
  }


  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Gestión de Recetas
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Gestiona recetas de elaborados y productos terminados con control de costos y trazabilidad.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleNewRecipe}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nueva Receta
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentDuplicateIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Recetas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {recipes.filter(r => r.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ScaleIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Elaborados
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {recipes.filter(r => r.type === 'elaborated' && r.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Productos Terminados
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {recipes.filter(r => r.type === 'finished' && r.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Costo Promedio
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    €{(recipes.filter(r => r.isActive).reduce((acc, r) => acc + r.cost, 0) / recipes.filter(r => r.isActive).length || 0).toFixed(2)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Buscar recetas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
        <div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as any)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="all">Todos los tipos</option>
            <option value="elaborated">Elaborados</option>
            <option value="finished">Productos Terminados</option>
          </select>
        </div>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'Todas las categorías' : category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Recipes List */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rendimiento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tiempo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dificultad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Costo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alérgenos
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecipes.map((recipe) => (
                    <tr key={recipe.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {recipe.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {recipe.code} • {recipe.category}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          recipe.type === 'elaborated' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {getTypeText(recipe.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {recipe.yield} {recipe.yieldUnit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {recipe.totalTime} min
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyColor(recipe.difficulty)}`}>
                          {getDifficultyText(recipe.difficulty)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">€{recipe.cost.toFixed(2)}</div>
                        {recipe.sellPrice && (
                          <div className="text-sm text-gray-500">
                            PVP: €{recipe.sellPrice.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {recipe.allergens.slice(0, 2).map((allergen) => (
                            <span
                              key={allergen}
                              className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                            >
                              {allergen}
                            </span>
                          ))}
                          {recipe.allergens.length > 2 && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                              +{recipe.allergens.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedRecipe(recipe)
                              setShowViewModal(true)
                            }}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditRecipe()}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRecipe(recipe.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* View Recipe Modal */}
      {showViewModal && selectedRecipe && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedRecipe.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedRecipe.code} • {getTypeText(selectedRecipe.type)}
                  </p>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Información General */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Información General</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Categoría</dt>
                        <dd className="text-sm text-gray-900">{selectedRecipe.category}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Rendimiento</dt>
                        <dd className="text-sm text-gray-900">{selectedRecipe.yield} {selectedRecipe.yieldUnit}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Tiempo Total</dt>
                        <dd className="text-sm text-gray-900">{selectedRecipe.totalTime} minutos</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Dificultad</dt>
                        <dd className="text-sm text-gray-900">{getDifficultyText(selectedRecipe.difficulty)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Costo</dt>
                        <dd className="text-sm text-gray-900">€{selectedRecipe.cost.toFixed(2)}</dd>
                      </div>
                      {selectedRecipe.sellPrice && (
                        <>
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Precio de Venta</dt>
                            <dd className="text-sm text-gray-900">€{selectedRecipe.sellPrice.toFixed(2)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Margen</dt>
                            <dd className="text-sm text-gray-900">{selectedRecipe.margin?.toFixed(1)}%</dd>
                          </div>
                        </>
                      )}
                    </dl>
                    <div className="mt-4">
                      <dt className="text-xs font-medium text-gray-500">Alérgenos</dt>
                      <dd className="mt-1 flex flex-wrap gap-1">
                        {selectedRecipe.allergens.map((allergen) => (
                          <span
                            key={allergen}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                          >
                            {allergen}
                          </span>
                        ))}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Ingredientes */}
                <div className="lg:col-span-2">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Ingredientes</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ingrediente
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cantidad
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            %
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Costo
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Preparación
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedRecipe.ingredients.map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.ingredient.name}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.quantity} {item.ingredient.unit}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.percentage.toFixed(1)}%
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              €{(item.quantity * item.ingredient.costPerUnit).toFixed(2)}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500">
                              {item.preparation || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Instrucciones */}
                <div className="lg:col-span-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Instrucciones</h4>
                  <div className="space-y-4">
                    {selectedRecipe.instructions.map((step) => (
                      <div key={step.id} className="flex">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {step.order}
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <p className="text-sm text-gray-900">{step.instruction}</p>
                          <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                            {step.duration && (
                              <span className="flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {step.duration} min
                              </span>
                            )}
                            {step.temperature && (
                              <span>🌡️ {step.temperature}°C</span>
                            )}
                            {step.equipment && (
                              <span>🔧 {step.equipment}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}